import React, { useState, useEffect } from 'react';
import { adminAPI } from '../utils/api';
import './Dashboard.css';
import './AdminDashboard.css';

const statusColors = {
  pending: { bg: '#FFF7ED', color: '#D97706' },
  confirmed: { bg: '#EFF6FF', color: '#1D4ED8' },
  completed: { bg: '#F0FDF4', color: '#16A34A' },
  cancelled: { bg: '#FEF2F2', color: '#DC2626' },
  in_progress: { bg: '#F0FDF4', color: '#16A34A' },
};

const AdminDashboard = () => {
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, bookingsRes, servicesRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers(),
        adminAPI.getBookings(),
        adminAPI.getServices(),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setBookings(bookingsRes.data);
      setServices(servicesRes.data);
    } catch (err) {}
    setLoading(false);
  };

  const toggleUserStatus = async (id, isActive) => {
    await adminAPI.updateUser(id, { isActive: !isActive });
    fetchAll();
  };

  const toggleVerify = async (id, isVerified) => {
    await adminAPI.updateUser(id, { isVerified: !isVerified });
    fetchAll();
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div>
            <h1>Admin Dashboard 🛡️</h1>
            <p>Monitor platform activity, manage users and ensure service quality</p>
          </div>
          <div className="admin-badge">Administrator</div>
        </div>

        {/* Stats */}
        <div className="admin-stats-grid">
          <div className="admin-stat-card blue">
            <div className="admin-stat-icon">👥</div>
            <div className="admin-stat-num">{stats.totalUsers || 0}</div>
            <div className="admin-stat-lbl">Total Customers</div>
          </div>
          <div className="admin-stat-card green">
            <div className="admin-stat-icon">🔧</div>
            <div className="admin-stat-num">{stats.totalProviders || 0}</div>
            <div className="admin-stat-lbl">Service Providers</div>
          </div>
          <div className="admin-stat-card purple">
            <div className="admin-stat-icon">📋</div>
            <div className="admin-stat-num">{stats.totalBookings || 0}</div>
            <div className="admin-stat-lbl">Total Bookings</div>
          </div>
          <div className="admin-stat-card orange">
            <div className="admin-stat-icon">⏳</div>
            <div className="admin-stat-num">{stats.pendingBookings || 0}</div>
            <div className="admin-stat-lbl">Pending Bookings</div>
          </div>
          <div className="admin-stat-card teal">
            <div className="admin-stat-icon">💰</div>
            <div className="admin-stat-num">Rs. {(stats.totalRevenue || 0).toLocaleString()}</div>
            <div className="admin-stat-lbl">Total Revenue</div>
          </div>
          <div className="admin-stat-card gold">
            <div className="admin-stat-icon">💎</div>
            <div className="admin-stat-num">Rs. {(stats.totalCommission || 0).toLocaleString()}</div>
            <div className="admin-stat-lbl">Platform Commission</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="dash-tabs">
          {['overview', 'users', 'bookings', 'services'].map(tab => (
            <button key={tab} className={`dash-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {loading ? <div className="dash-loading">Loading data...</div> : (
          <>
            {/* Overview */}
            {activeTab === 'overview' && (
              <div>
                <h3 className="section-title">Recent Bookings</h3>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Customer</th>
                        <th>Provider</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Commission</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.slice(0, 8).map(b => {
                        const s = statusColors[b.status] || statusColors.pending;
                        return (
                          <tr key={b._id}>
                            <td className="td-bold">{b.service?.title}</td>
                            <td>{b.customer?.name}</td>
                            <td>{b.provider?.name}</td>
                            <td>{new Date(b.bookingDate).toLocaleDateString()}</td>
                            <td className="td-blue">Rs. {b.totalAmount}</td>
                            <td className="td-green">Rs. {b.commissionAmount?.toFixed(0)}</td>
                            <td><span className="status-badge" style={{ background: s.bg, color: s.color }}>{b.status}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Users */}
            {activeTab === 'users' && (
              <div>
                <h3 className="section-title">All Users ({users.length})</h3>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>City</th>
                        <th>Joined</th>
                        <th>Verified</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u._id}>
                          <td className="td-bold">{u.name}</td>
                          <td>{u.email}</td>
                          <td>
                            <span className="role-badge" style={{
                              background: u.role === 'admin' ? '#FEF2F2' : u.role === 'provider' ? '#EFF6FF' : '#F0FDF4',
                              color: u.role === 'admin' ? '#DC2626' : u.role === 'provider' ? '#1D4ED8' : '#16A34A'
                            }}>{u.role}</span>
                          </td>
                          <td>{u.city || '—'}</td>
                          <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                          <td>
                            <span style={{ color: u.isVerified ? '#16A34A' : '#94A3B8', fontWeight: '600', fontSize: '13px' }}>
                              {u.isVerified ? '✅ Yes' : '⬜ No'}
                            </span>
                          </td>
                          <td>
                            <span style={{ color: u.isActive ? '#16A34A' : '#DC2626', fontWeight: '600', fontSize: '13px' }}>
                              {u.isActive ? 'Active' : 'Suspended'}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              {u.role === 'provider' && (
                                <button className="action-btn verify" onClick={() => toggleVerify(u._id, u.isVerified)}>
                                  {u.isVerified ? 'Unverify' : 'Verify'}
                                </button>
                              )}
                              <button className="action-btn" style={{ background: u.isActive ? '#FEE2E2' : '#F0FDF4', color: u.isActive ? '#DC2626' : '#16A34A' }}
                                onClick={() => toggleUserStatus(u._id, u.isActive)}>
                                {u.isActive ? 'Suspend' : 'Activate'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Bookings */}
            {activeTab === 'bookings' && (
              <div>
                <h3 className="section-title">All Bookings ({bookings.length})</h3>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Customer</th>
                        <th>Provider</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th>City</th>
                        <th>Total</th>
                        <th>Commission (15%)</th>
                        <th>Provider Earns</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map(b => {
                        const s = statusColors[b.status] || statusColors.pending;
                        return (
                          <tr key={b._id}>
                            <td className="td-bold">{b.service?.title}</td>
                            <td>{b.customer?.name}</td>
                            <td>{b.provider?.name}</td>
                            <td>{new Date(b.bookingDate).toLocaleDateString()}</td>
                            <td>{b.timeSlot}</td>
                            <td>{b.city}</td>
                            <td className="td-blue">Rs. {b.totalAmount}</td>
                            <td className="td-green">Rs. {b.commissionAmount?.toFixed(0)}</td>
                            <td>Rs. {b.providerEarning?.toFixed(0)}</td>
                            <td><span className="status-badge" style={{ background: s.bg, color: s.color }}>{b.status}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Services */}
            {activeTab === 'services' && (
              <div>
                <h3 className="section-title">All Services ({services.length})</h3>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Provider</th>
                        <th>City</th>
                        <th>Price</th>
                        <th>Rating</th>
                        <th>Reviews</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {services.map(s => (
                        <tr key={s._id}>
                          <td className="td-bold">{s.title}</td>
                          <td><span className="role-badge" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>{s.category}</span></td>
                          <td>{s.provider?.name}</td>
                          <td>{s.city}</td>
                          <td className="td-blue">Rs. {s.price}</td>
                          <td>⭐ {s.rating?.toFixed(1) || 'N/A'}</td>
                          <td>{s.totalReviews || 0}</td>
                          <td><span style={{ color: s.isActive ? '#16A34A' : '#DC2626', fontWeight: '600', fontSize: '13px' }}>{s.isActive ? 'Active' : 'Inactive'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
